import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes, FaClock } from 'react-icons/fa';
import { FiSearch, FiX, FiClock, FiTrendingUp } from 'react-icons/fi';
import { debounce, SearchHistory } from '../utils/searchUtils';
import { GetSuggestions } from '../Data';

/**
 * HeaderSearchInput - Simplified search component designed for header integration
 * Uses the same modern functionality but with header-specific layout
 */
const HeaderSearchInput = ({ isMobile = false, className = '' }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => SearchHistory.get());
  const [popularSearches, setPopularSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const suggestionsRef = useRef(null);

  // Load popular searches from database on component mount
  useEffect(() => {
    const loadPopularSearches = async () => {
      try {
        // Get actual popular/trending searches from database
        const trends = await GetSuggestions('trending');
        if (trends && trends.length > 0) {
          setPopularSearches(trends.slice(0, 8));
        } else {
          // If no trending data available, don't show popular searches section
          setPopularSearches([]);
        }
      } catch (error) {
        console.error('Error fetching popular searches:', error);
        // Don't show popular searches if API fails
        setPopularSearches([]);
      }
    };

    loadPopularSearches();
  }, []);

  // Debounced suggestions
  const debouncedGetSuggestions = useRef(
    debounce(async (searchTerm) => {
      if (searchTerm.length < 2) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }
      
      try {
        const results = await GetSuggestions(searchTerm);
        setSuggestions(results || []);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        // Fallback suggestions
        setSuggestions([
          `${searchTerm} pro`,
          `${searchTerm} max`,
          `best ${searchTerm}`,
          `${searchTerm} 2024`
        ]);
      } finally {
        setIsLoading(false);
      }
    }, 300)
  ).current;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    
    if (value.length >= 2) {
      setIsLoading(true);
      setShowSuggestions(true);
      debouncedGetSuggestions(value);
    } else if (value.length === 0) {
      setSuggestions([]);
      setShowSuggestions(true); // Show recent/popular searches
      setIsLoading(false);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
    }
  };

  const handleSearch = useCallback((searchTerm = query) => {
    if (searchTerm.trim()) {
      // Add to recent searches
      const updated = SearchHistory.add(searchTerm.trim());
      setRecentSearches(updated);
      
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      setShowSuggestions(false);
    }
  }, [query, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center">
          {/* Search Icon or Loading */}
          <div className="absolute left-3 flex items-center">
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            ) : (
              <FaSearch className="h-4 w-4 text-gray-400" />
            )}
          </div>
          
          {/* Search Input */}
          <input
            type="text"
            value={query}
            onChange={handleSearchChange}
            onFocus={() => setShowSuggestions(true)}
            onBlur={(e) => {
              if (!suggestionsRef.current || 
                  !suggestionsRef.current.contains(e.relatedTarget)) {
                setTimeout(() => setShowSuggestions(false), 150);
              }
            }}
            className={`w-full pl-10 pr-12 py-3 text-gray-800 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent ${
              isMobile ? 'text-base' : 'text-sm'
            }`}
            placeholder={isMobile ? "Search products..." : "Search for products, brands, categories..."}
            autoComplete="off"
          />
          
          {/* Clear Button */}
          {query && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-12 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FaTimes className="h-3 w-3" />
            </button>
          )}
          
          {/* Search Submit Button */}
          <button 
            type="submit"
            className="absolute right-2 p-2 bg-yellow-400 hover:bg-yellow-500 text-gray-800 rounded-md flex items-center justify-center transition-colors"
          >
            <FaSearch className="h-4 w-4" />
          </button>
        </div>
      </form>
      
      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div 
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto"
        >
          {/* Live Suggestions */}
          {query.length >= 2 && suggestions.length > 0 && (
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Suggestions
              </div>
              {suggestions.slice(0, isMobile ? 4 : 6).map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <FaSearch className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-900">{suggestion}</span>
                </button>
              ))}
            </div>
          )}
          
          {/* Recent Searches */}
          {query.length === 0 && recentSearches.length > 0 && (
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <FaClock className="h-3 w-3" />
                {isMobile ? 'Recent' : 'Recent Searches'}
              </div>
              {recentSearches.slice(0, isMobile ? 3 : 5).map((search, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(search)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 transition-colors"
                >
                  <FaClock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-900">{search}</span>
                </button>
              ))}
            </div>
          )}
          
          {/* Popular Searches - only show if available from database */}
          {query.length === 0 && popularSearches.length > 0 && (
            <div className="p-2 border-t border-gray-100">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <FiTrendingUp className="h-3 w-3" />
                {isMobile ? 'Popular' : 'Popular Searches'}
              </div>
              <div className={isMobile ? 'space-y-1' : 'grid grid-cols-2 gap-1'}>
                {popularSearches.slice(0, isMobile ? 3 : 6).map((search, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(search)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3 transition-colors"
                  >
                    <FiTrendingUp className="h-3 w-3 text-blue-500 flex-shrink-0" />
                    <span className="text-gray-900 truncate text-sm">{search}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* No Suggestions */}
          {query.length >= 2 && suggestions.length === 0 && !isLoading && (
            <div className="p-6 text-center text-gray-500">
              <FaSearch className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No suggestions found</p>
              <p className="text-xs text-gray-400 mt-1">
                Press Enter to search for "{query}"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearchInput;